import { Form } from "antd";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { RoleForm } from "./RoleFormDialog.default";
import { useEffect, useState } from "react";
import {
  addCompanyUserRole,
  deleteCompanyUserRole,
  updateCompanyUserRole,
  getCompanies,
} from "../repository/data.repository";
import DynamicForm from "../controls/DynamicForm";
import { QueryOperation } from "../code/search-query/search-query";
import { nameOf } from "../utils/typescript";
import { retry, getProblemMessage } from "../utils/http";
import { Company } from "../repository/data.default";
import InModal from "../controls/InModal";
import { DynamicSchema } from "../controls/DynamicForm.default";
import { setFormFields } from "../utils/setFormFields";
import { hasFormTouched } from "../utils/hasFormChanged";

export interface RoleFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete" | "view";
  readonly initialValues?: RoleForm;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function RoleFormDialog(props: RoleFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<RoleForm>([], form);
  const [formSchema, setFormSchema] = useState<DynamicSchema>(null!);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (props.open) {
      if (props.initialValues) setFormFields(form, props.initialValues);
      else form.setFieldsValue(props.initialValues);
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
    setFormSchema(createSchema());
  }, [form, values, props.open]);

  // Events
  function onSubmit(): void {
    const { name, description, isManager, company } = values;
    setLoading(true);
    setErrorMessage("");
    if (props.mode === "add") {
      addCompanyUserRole({
        name,
        description,
        isManager,
        companyUid: company?.value,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "update") {
      if (hasFormTouched(form)) {
        updateCompanyUserRole({
          uid: props.initialValues?.uid,
          name,
          isManager,
          description,
          rowVersion: props.initialValues?.rowVersion,
        })
          .then(() => {
            setLoading(false);
            props.onFinish?.();
          })
          .catch((err) => {
            setLoading(false);
            setErrorMessage(getProblemMessage(err));
          });
      } else {
        setLoading(false);
        props.onFinish?.();
      }
    } else if (props.mode === "delete") {
      deleteCompanyUserRole(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function createSchema(): DynamicSchema {
    return {
      groups: [
        {
          controls: [
            // Role identity first — what the role is.
            {
              type: "textbox",
              name: "name",
              label: "Name",
              rules: [
                {
                  required: true,
                  message: "Name is required",
                },
              ],
            },
            {
              type: "areabox",
              name: "description",
              label: "Description",
              rows: 3,
            },
            {
              // Management roles use the Company App; employee roles use the Employee App.
              type: "togglebox",
              name: "isManager",
              label: "Role level",
              checkedText: "Management",
              unCheckedText: "Employee",
            },
            // Placement — which company owns the role.
            {
              type: props.mode == "add" ? "combobox" : "labelbox",
              name: "company",
              label: "Company",
              request: companiesRequest,
              // A role always belongs to a company (assignment target) — required on create.
              rules:
                props.mode === "add"
                  ? [{ required: true, message: "Company is required" }]
                  : undefined,
            },
          ],
        },
      ],
    };
  }

  function reset(): void {
    setValid(false);
    setErrorMessage("");
    setLoading(false);
    form.resetFields();
  }

  const companiesRequest = (search: string) => {
    return retry(() =>
      getCompanies({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Company>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Role", props.initialValues?.name)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode !== "delete" && (
        <DynamicForm className="mb-4 w-auto" form={form} schema={formSchema} />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="role"
          entityName={props.initialValues?.name}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default RoleFormDialog;
